/**
 * お知らせバナーの表示スタイル定義 (steering 488)
 *
 * `level` (意味) と `style` (見た目) は独立した別概念で、両者の組み合わせで
 * Tailwind クラスが決まる。色相は既存 `TOAST_STYLES`
 * (`components/common/toastStyles.ts`) に揃え、light / dark 双方を定義する。
 *
 * **toast との意図的な差異**: toast の light `error` は反転 (`bg-red-600` + 白文字) だが、
 * バナーは画面上部に**常時**表示されるため反転を使わず淡色背景 + 濃色テキストで統一する
 * (常時反転色があると本文の可読性を落とすため)。
 *
 * **未マップ値の fallback は必須** (§2.4) — BE の enum が増えても
 * `STYLES[level]` が undefined を返して render で落ちないようにする。
 * 過去に `STATUS_CONFIG[item.status]` の undefined でツリー全体がアンマウントした
 * 事故がある (#255)。
 */
import type { AnnouncementLevel, AnnouncementStyle } from '@/types';

export interface AnnouncementLevelStyle {
  /** 塗りつぶし時のコンテナ (背景 + 枠線) */
  filled: string;
  /** アウトライン時のコンテナ (透過背景 + 色付き枠線) */
  outline: string;
  /** 最小表示時のコンテナ (背景・枠線なし) */
  minimal: string;
  /** アイコン色 */
  icon: string;
  /** 本文テキスト色 */
  text: string;
  /** リンク色 */
  link: string;
  /** 閉じるボタン色 */
  close: string;
  /** スクリーンリーダー向けのレベル名 */
  label: string;
}

const LEVEL_STYLES: Record<AnnouncementLevel, AnnouncementLevelStyle> = {
  info: {
    filled: 'bg-blue-50 border border-blue-200 dark:bg-blue-900/40 dark:border-blue-700',
    outline: 'bg-transparent border border-blue-400 dark:border-blue-600',
    minimal: 'bg-transparent border border-transparent',
    icon: 'text-blue-600 dark:text-blue-300',
    text: 'text-blue-900 dark:text-blue-100',
    link: 'text-blue-700 hover:text-blue-900 dark:text-blue-200 dark:hover:text-white',
    close: 'text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-white',
    label: 'お知らせ',
  },
  success: {
    filled: 'bg-green-50 border border-green-200 dark:bg-green-900/40 dark:border-green-700',
    outline: 'bg-transparent border border-green-400 dark:border-green-600',
    minimal: 'bg-transparent border border-transparent',
    icon: 'text-green-600 dark:text-green-300',
    text: 'text-green-900 dark:text-green-100',
    link: 'text-green-700 hover:text-green-900 dark:text-green-200 dark:hover:text-white',
    close: 'text-green-600 hover:text-green-800 dark:text-green-300 dark:hover:text-white',
    label: '完了',
  },
  warning: {
    filled: 'bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/40 dark:border-yellow-700',
    outline: 'bg-transparent border border-yellow-400 dark:border-yellow-600',
    minimal: 'bg-transparent border border-transparent',
    icon: 'text-yellow-700 dark:text-yellow-300',
    text: 'text-yellow-900 dark:text-yellow-100',
    link: 'text-yellow-800 hover:text-yellow-900 dark:text-yellow-200 dark:hover:text-white',
    close: 'text-yellow-700 hover:text-yellow-900 dark:text-yellow-300 dark:hover:text-white',
    label: '注意',
  },
  error: {
    filled: 'bg-red-50 border border-red-200 dark:bg-red-900/40 dark:border-red-700',
    outline: 'bg-transparent border border-red-400 dark:border-red-600',
    minimal: 'bg-transparent border border-transparent',
    icon: 'text-red-600 dark:text-red-300',
    text: 'text-red-900 dark:text-red-100',
    link: 'text-red-700 hover:text-red-900 dark:text-red-200 dark:hover:text-white',
    close: 'text-red-600 hover:text-red-800 dark:text-red-300 dark:hover:text-white',
    label: '障害・エラー',
  },
};

/**
 * 未マップ level の fallback。
 *
 * BE の enum が増えたときに render を落とさないための安全網で、
 * 中立なグレー表示にする (誤った深刻度を演出しない)。
 */
const FALLBACK_LEVEL_STYLE: AnnouncementLevelStyle = {
  filled: 'bg-gray-50 border border-gray-200 dark:bg-gray-800 dark:border-gray-600',
  outline: 'bg-transparent border border-gray-400 dark:border-gray-500',
  minimal: 'bg-transparent border border-transparent',
  icon: 'text-gray-600 dark:text-gray-300',
  text: 'text-gray-900 dark:text-gray-100',
  link: 'text-gray-700 hover:text-gray-900 dark:text-gray-200 dark:hover:text-white',
  close: 'text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white',
  label: 'お知らせ',
};

/** level のスタイル一式を取得する (未マップ値は fallback)。 */
export function getAnnouncementLevelStyle(level: AnnouncementLevel): AnnouncementLevelStyle {
  return LEVEL_STYLES[level] ?? FALLBACK_LEVEL_STYLE;
}

/** level x style からコンテナのクラスを決める (未マップ style は filled 相当)。 */
export function getAnnouncementContainerClass(
  level: AnnouncementLevel,
  style: AnnouncementStyle,
): string {
  const levelStyle = getAnnouncementLevelStyle(level);
  if (style === 'outline') return levelStyle.outline;
  if (style === 'minimal') return levelStyle.minimal;
  return levelStyle.filled;
}

/** 一覧バッジ用の日本語ラベル。 */
export const ANNOUNCEMENT_LEVEL_LABELS: Record<AnnouncementLevel, string> = {
  info: 'Info (情報)',
  success: 'Success (成功・完了)',
  warning: 'Warning (注意)',
  error: 'Error (障害・エラー)',
};

export function getAnnouncementLevelLabel(level: AnnouncementLevel): string {
  return ANNOUNCEMENT_LEVEL_LABELS[level] ?? String(level);
}

/** 表示スタイルの日本語ラベル。 */
export const ANNOUNCEMENT_STYLE_LABELS: Record<AnnouncementStyle, string> = {
  filled: '塗りつぶし (デフォルト)',
  outline: 'アウトライン',
  minimal: '最小 (アイコン + テキスト)',
};

export function getAnnouncementStyleLabel(style: AnnouncementStyle): string {
  return ANNOUNCEMENT_STYLE_LABELS[style] ?? String(style);
}

/** 一覧バッジの variant (既存 `BadgeVariant` に合わせる)。 */
export const ANNOUNCEMENT_LEVEL_BADGE: Record<
  AnnouncementLevel,
  'blue' | 'green' | 'amber' | 'red'
> = {
  info: 'blue',
  success: 'green',
  warning: 'amber',
  error: 'red',
};

export function getAnnouncementLevelBadge(
  level: AnnouncementLevel,
): 'blue' | 'green' | 'amber' | 'red' | 'gray' {
  return ANNOUNCEMENT_LEVEL_BADGE[level] ?? 'gray';
}
